import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import '@fontsource/material-icons/index.css'
import './NSWTransitPage.css'
import { useVehiclePositions } from '../hooks/useVehiclePositions'
import { markerService } from '../services/map/markers'
import { MAP_CONFIG } from '../utils/constants'
import type { TransitServiceType } from '../types/api'

const SERVICE_DESCRIPTIONS: Record<TransitServiceType, string> = {
  'sydney-metro': 'Metro lines M1–M12 across Greater Sydney',
  'sydney-trains': 'Train lines T1–T9 across the Sydney network',
  'light-rail-innerwest': 'L1 Dulwich Hill line through the Inner West',
}

const NSWTransitPage = () => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [serviceType, setServiceType] = useState<TransitServiceType>('sydney-metro')

  const {
    vehicles: _vehiclePositions,
    isLoading: isLoadingVehicles,
    lastUpdate,
    error: _errorVehicles,
    fetchVehicles,
    vehicleCount
  } = useVehiclePositions({
    autoRefresh: true,
    refreshInterval: 30000,
    serviceType: serviceType
  })

  const handleServiceTypeChange = (newServiceType: TransitServiceType) => {
    setServiceType(newServiceType)
    // Clear existing markers when switching services
    markerService.clearVehicleMarkers()
  }

  useEffect(() => {
    // Wait for the container to be available
    const timer = setTimeout(() => {
      if (map.current) return

      if (mapContainer.current) {
        try {
          const isDebug = import.meta.env.VITE_DEBUG === 'true'
          
          if (isDebug) {
            // Debug mode: Use OpenStreetMap as base with OpenRailwayMap overlay
            map.current = new maplibregl.Map({
              container: mapContainer.current,
              style: {
                version: 8,
                sources: {
                  'osm': {
                    type: 'raster',
                    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                    tileSize: 256,
                    attribution: '© OpenStreetMap contributors'
                  },
                  'railway': {
                    type: 'raster',
                    tiles: ['https://tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png'],
                    tileSize: 256,
                    attribution: '© OpenRailwayMap contributors'
                  }
                },
                layers: [
                  {
                    id: 'osm-tiles',
                    type: 'raster',
                    source: 'osm',
                    minzoom: 0,
                    maxzoom: 22
                  },
                  {
                    id: 'railway-overlay',
                    type: 'raster',
                    source: 'railway',
                    paint: {
                      'raster-opacity': 0.6
                    },
                    minzoom: 0,
                    maxzoom: 22
                  }
                ]
              },
              center: MAP_CONFIG.SYDNEY_CENTER,
              zoom: MAP_CONFIG.DEFAULT_ZOOM
            })
            
            map.current.on('load', () => {
              setMapLoaded(true)
              // Set up marker service and fetch initial vehicle positions
              markerService.setMap(map.current!)
              fetchVehicles()
            })
          } else {
            // Production mode: Use MapTiler with OpenRailwayMap overlay
            const maptilerKey = import.meta.env.VITE_MAPTILER_KEY
            if (!maptilerKey) {
              throw new Error('MapTiler API key not found. Please set VITE_MAPTILER_KEY in your .env file')
            }

            map.current = new maplibregl.Map({
              container: mapContainer.current,
              style: `https://api.maptiler.com/maps/streets/style.json?key=${maptilerKey}`,
              center: MAP_CONFIG.SYDNEY_CENTER,
              zoom: MAP_CONFIG.DEFAULT_ZOOM
            })

            map.current.on('load', () => {
              // Add OpenRailwayMap as an overlay layer
              map.current?.addSource('railway-overlay', {
                type: 'raster',
                tiles: ['https://tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution: '© OpenRailwayMap contributors'
              })

              map.current?.addLayer({
                id: 'railway-overlay-layer',
                type: 'raster',
                source: 'railway-overlay',
                paint: {
                  'raster-opacity': 0.6
                }
              })

              setMapLoaded(true)
              // Set up marker service and fetch initial vehicle positions
              markerService.setMap(map.current!)
              fetchVehicles()
            })
          }

          map.current.on('error', (e) => {
            console.error('Map error:', e)
            setMapLoaded(false)
          })
        } catch (error) {
          console.error('Failed to initialize map:', error)
          setMapLoaded(false)
        }
      }
    }, 500)

    return () => {
      clearTimeout(timer)
      if (map.current) {
        map.current.remove()
      }
    }
    // Map must initialize exactly once on mount; re-running on `fetchVehicles`
    // identity change would tear down and re-create the entire map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])



  const quickLocations: { icon: string; name: string; desc: string; coords: [number, number] }[] = [
    { icon: 'directions_railway', name: 'Central Station',                  desc: "Sydney's main transport hub",        coords: [151.2093, -33.8688] },
    { icon: 'subway',             name: 'Town Hall Station',                desc: 'CBD underground station',            coords: [151.2070, -33.8734] },
    { icon: 'flight',             name: 'Sydney Airport (SYD)',             desc: 'Kingsford Smith — Mascot',           coords: [151.1753, -33.9399] },
    { icon: 'flight_takeoff',     name: 'Western Sydney Intl (WSI)',        desc: 'Nancy-Bird Walton — Badgerys Creek', coords: [150.7253, -33.8800] },
  ]

  const isDebugMode = import.meta.env.VITE_DEBUG === 'true'

  return (
    <div className="nsw-transit">

      {/* ── Top app bar ─────────────────────────────────────────── */}
      <header className="md-top-app-bar tbar">
        <Link to="/geocartography" className="md-icon-button" aria-label="Back to GeoCartography">
          <span className="material-icons" aria-hidden="true">arrow_back</span>
        </Link>
        <div className="tbar__title">
          <h1 className="md-title-large">NSW Transit Map</h1>
          <span className="sub md-body-small">Greater Sydney · live network</span>
        </div>
        <div className="tbar__spacer" />
        <button
          type="button"
          className="md-button md-button--tonal has-icon"
          onClick={() => map.current?.flyTo({ center: MAP_CONFIG.SYDNEY_CENTER, zoom: MAP_CONFIG.DEFAULT_ZOOM })}
        >
          <span className="material-icons" aria-hidden="true">restart_alt</span>
          Reset view
        </button>
      </header>

      {/* ── Main ──────────────────────────────────────────────── */}
      <div className="main">

        {/* Sidebar */}
        <aside className="panel" aria-label="Transit controls">

          {/* Service */}
          <section className="panel__section">
            <span className="overline md-label-medium">Service</span>
            <div className="md-segmented" role="group" aria-label="Select service type">
              <button
                type="button"
                className="md-segmented__seg"
                aria-pressed={serviceType === 'sydney-metro'}
                onClick={() => handleServiceTypeChange('sydney-metro')}
              >
                <span className="material-icons" aria-hidden="true">subway</span>
                Metro
              </button>
              <button
                type="button"
                className="md-segmented__seg"
                aria-pressed={serviceType === 'sydney-trains'}
                onClick={() => handleServiceTypeChange('sydney-trains')}
              >
                <span className="material-icons" aria-hidden="true">train</span>
                Trains
              </button>
              <button
                type="button"
                className="md-segmented__seg"
                aria-pressed={serviceType === 'light-rail-innerwest'}
                onClick={() => handleServiceTypeChange('light-rail-innerwest')}
              >
                <span className="material-icons" aria-hidden="true">tram</span>
                Light Rail
              </button>
            </div>
            <p className="caption md-body-small">
              {SERVICE_DESCRIPTIONS[serviceType]}
            </p>
          </section>

          {/* Live vehicles */}
          <section className="panel__section">
            <div className="live-card">
              <div className="live-card__top">
                <span className="live-dot">
                  <span className="pulse"><i /></span>
                  <span className="md-label-medium">Live vehicles</span>
                </span>
                <span className="md-badge">Active</span>
              </div>
              <div className="live-count">
                <b>{vehicleCount}</b>
                <span className="md-body-medium">tracked</span>
              </div>
              <p className="live-meta md-body-small">
                {lastUpdate ? `Updated ${lastUpdate} · ` : ''}auto-refresh every 30 s
              </p>
              <button
                type="button"
                className="md-button md-button--filled has-icon"
                style={{ width: '100%' }}
                onClick={fetchVehicles}
                disabled={isLoadingVehicles}
              >
                <span className="material-icons" aria-hidden="true">refresh</span>
                {isLoadingVehicles ? 'Refreshing…' : 'Refresh now'}
              </button>
            </div>
          </section>

          {/* Quick locations */}
          <section className="panel__section">
            <span className="overline md-label-medium">Quick locations</span>
            <ul className="loc-list">
              {quickLocations.map(({ icon, name, desc, coords }) => (
                <li key={name}>
                  <button
                    type="button"
                    className="loc-item"
                    onClick={() => map.current?.flyTo({ center: coords, zoom: 15 })}
                  >
                    <span className="avatar">
                      <span className="material-icons" aria-hidden="true">{icon}</span>
                    </span>
                    <span className="txt">
                      <b>{name}</b>
                      <span>{desc}</span>
                    </span>
                    <span className="go material-icons" aria-hidden="true">arrow_forward</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {/* Tech chips */}
          <section className="panel__section" style={{ borderBottom: 'none' }}>
            <span className="overline md-label-medium">Data &amp; rendering</span>
            <div className="tech-chips">
              <span className="md-chip">MapLibre GL</span>
              <span className="md-chip">{isDebugMode ? 'OpenStreetMap' : 'MapTiler'}</span>
              <span className="md-chip">OpenRailwayMap</span>
              <span className="md-chip">React</span>
            </div>
          </section>
        </aside>

        {/* Map area — initialization and ref untouched */}
        <div className="map-area">
          <div ref={mapContainer} className="map-canvas" />
          {!mapLoaded && (
            <div className="map-loading">
              <div className="spinner" role="status" aria-label="Loading map" />
              <p className="md-body-medium">Loading map…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NSWTransitPage
